/**
 * © [2020] Conduent, Inc. All rights reserved. Conduent®and Conduent Agile
 * Star®are trademarks of Conduent, Inc. and/or its subsidiaries in the United
 * States and/or other countries.
 */
import React, { useMemo, useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import Grid from '../../../../SharedModules/Table/TableGridWithFeatures';
import CustomDateComponent from '../../NDC/component/CustomDateComponent';
import { DISPLAY_CHECK_LIST_TABLEHEAD } from '../utils.js/tablehead';
import DisplayCheckActionComponent from './DisplayCheckActionComponent';
import DisplayCheckPriceComponent from './DisplayCheckPriceComponent';
import ViewHistoryComponent from '../../../../SharedModules/MicroFrontend/ViewHistoryComponent';
import {
  navigateToNextCell,
  onCellFocused,
  tabToNextCell,
} from '../../../../SharedModules/AgGridUtilities/suppressNavigation';
import {
  removeCommaForNumberFiled,
  removeDollarForNumberFiled,
} from '../../../../SharedModules/CommonUtilities/commonUtilities';

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const DisplayCheckTableComponent = ({
  tableData = [],
  referenceData,
  systemParameter,
}) => {
  const [fullScreenView, setFullScreenView] = useState(false);
  const [showViewHistory, setShowViewHistory] = useState(false);
  const [keyValue, setKeyValue] = useState([]);
  const displayData = [...tableData];

  const calculateTotalSum = (ColName, rowData) => {
    if (ColName === 'checkLogNum') {
      const data = rowData.length;
      return `${parseFloat(data)?.toLocaleString('en-US')} Checks`;
    }
    const tempObj = [
      ...rowData?.map((item) => {
        item[ColName] = item[ColName]
      ? parseFloat(String(item[ColName] ?? '0').replace(/,/g, '').trim())
          : 0;
        return item;
      }),
    ];
    const data = tempObj?.reduce((a, b) => {
      return a + parseFloat(b[ColName]);
    }, 0);
    return data;
  };

  const createData = (count) => {
    const result = [];
    for (let i = 0; i < count; i += 1) {
      result.push({
        isBottomRow: true,
        checkLogNum: calculateTotalSum('checkLogNum', tableData),
        checkAmt: calculateTotalSum(
          'checkAmt',
          displayData?.map((item) => {
            if (item.checkAmt?.toString()?.includes('$')) {
              item.checkAmt = item.checkAmt?.toString()?.split('$')[1];
            }
            if (item.totAllocatedAmt?.toString()?.includes('$')) {
              item.totAllocatedAmt = item.totAllocatedAmt
                ?.toString()
                ?.split('$')[1];
            }
            return item;
          })
        ),
        totAllocatedAmt: calculateTotalSum(
          'totAllocatedAmt',
          displayData?.map((item) => {
            if (item.checkAmt?.toString()?.includes('$')) {
              item.checkAmt = item.checkAmt?.toString()?.split('$')[1];
            }
            if (item.totAllocatedAmt?.toString()?.includes('$')) {
              item.totAllocatedAmt = item.totAllocatedAmt
                ?.toString()
                ?.split('$')[1];
            }
            return item;
          })
        ),
      });
    }
    return result;
  };

  const pinnedBottomRowData = useMemo(() => {
    return createData(1, 'Bottom');
  }, [tableData && tableData.length]);

  const handleViewHistory = (gridApi) => {
    const selectedRow = gridApi.getSelectedRows()?.[0]?.checkLogNum;
    if (selectedRow !== undefined) {
      setShowViewHistory(true);
      setKeyValue({ CHECK_LOG_NUM: selectedRow });
    }
  };

  const handleFullScreenViewClick = () => {
    setFullScreenView(!fullScreenView);
  };

  return (
    <div className="table-container col-12 no-border frr-tbl-with-feature">
      <Grid
        gridHeight={fullScreenView ? '100vh' : 500}
        rowData={tableData}
        defaultCol={{ editable: false }}
        columnDefs={DISPLAY_CHECK_LIST_TABLEHEAD(
          referenceData,
          (systemParameter &&
            systemParameter.paramGeneralVO &&
            systemParameter.paramGeneralVO[0]) ||
            null
        )}
        data-testid="onEditingStopped"
        enterMovesDown
        suppressCellFocus
        onCellFocused={onCellFocused}
        singleClickEdit={false}
        navigateToNextCell={navigateToNextCell}
        // tabToNextCell={tabToNextCell}
        handleFullScreenViewClick={handleFullScreenViewClick}
        exportFile
        animateRows
        sideBar={false}
        isScrollOn
        fileName="Display Check List"
        handleViewHistory={handleViewHistory}
        pinnedBottomRowData={pinnedBottomRowData}
        frameworkComponents={{
          actionRenderer: DisplayCheckActionComponent,
          dateComponent: CustomDateComponent,
          priceComponent: DisplayCheckPriceComponent,
          noDataComponent: () => <></>,
          totalText: () => <span className="semiBoldText">Totals:</span>,
          NameRenderCheckIdentifier: (cellInfo) => {
            const name = cellInfo?.data?.checkIdentifier;
            if (name && name.length > 15) {
              return (
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id="labeler-tooltip">{name}</Tooltip>}
                >
                  <div>{name}</div>
                </OverlayTrigger>
              );
            }
            return <div>{name}</div>;
          },
          checkLogNumberRender: (cellInfo) => {
            return (
              <div
                className={cellInfo?.data?.isBottomRow ? 'semiBoldText' : ''}
              >
                {cellInfo?.data?.checkLogNum}
              </div>
            );
          },
          NameRenderBatchNum: (cellInfo) => {
            const name = cellInfo?.data?.batchNum;
            if (name && name.length > 15) {
              return (
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id="labeler-tooltip">{name}</Tooltip>}
                >
                  <div>{name}</div>
                </OverlayTrigger>
              );
            }
            return <div>{name}</div>;
          },
          NameRenderCheckIssuer: (cellInfo) => {
            const name = cellInfo?.data?.checkIssuer;
            if (name && name.length > 22) {
              return (
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id="labeler-tooltip">{name}</Tooltip>}
                >
                  <div>{name?.trim()}</div>
                </OverlayTrigger>
              );
            }
            return <div>{name?.trim()}</div>;
          },
          ccnRenderer: (cellInfo) => {
            const name =
              cellInfo && cellInfo.data && cellInfo.data.checkIdentifier;
            if (name && name.length > 20) {
              return (
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id="labeler-tooltip">{name}</Tooltip>}
                >
                  <div>{name}</div>
                </OverlayTrigger>
              );
            }
            return <div>{name}</div>;
          },
          batchNumRenderer: (cellInfo) => {
            const name = cellInfo && cellInfo.data && cellInfo.data.batchNum;
            if (name && name.length > 20) {
              return (
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id="labeler-tooltip">{name}</Tooltip>}
                >
                  <div>{name}</div>
                </OverlayTrigger>
              );
            }
            return <div>{name}</div>;
          },
          NameRenderInformation: (cellInfo) => {
            const name = cellInfo?.data?.checkInfo;
            if (name && name.length > 15) {
              return (
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id="labeler-tooltip">{name}</Tooltip>}
                >
                  <div>{name}</div>
                </OverlayTrigger>
              );
            }
            return <div>{name}</div>;
          },
          CheckAmtCellRender: (cellInfo) => {
            const isNegativeVal = !(
              parseFloat(
                removeCommaForNumberFiled(
                  removeDollarForNumberFiled(cellInfo?.data?.checkAmt || 0)
                )
              ) >= 0
            );
            return (
              <>
                {isNegativeVal ? (
                  <div
                    className={
                      cellInfo?.data?.isBottomRow ? 'semiBoldText' : ''
                    }
                    style={{ color: '#eb0000' }}
                  >
                    {`(${formatter
                      .format(-cellInfo?.data?.checkAmt || 0)
                      .replace(/^(\D+)/, '$1 ')})`}
                  </div>
                ) : (
                  <div
                    className={
                      cellInfo?.data?.isBottomRow ? 'semiBoldText' : ''
                    }
                  >
                    {formatter
                      .format(cellInfo?.data?.checkAmt || 0)
                      .replace(/^(\D+)/, '$1 ')}
                  </div>
                )}
              </>
            );
          },
          TotAllocatedAmtCellRender: (cellInfo) => {
            const isNegativeVal = !(
              parseFloat(cellInfo?.data?.totAllocatedAmt) >= 0
            );
            return (
              <>
                {isNegativeVal ? (
                  <div
                    className={
                      cellInfo?.data?.isBottomRow ? 'semiBoldText' : ''
                    }
                    style={{ color: '#eb0000' }}
                  >
                    {`(${formatter
                      .format(-cellInfo?.data?.totAllocatedAmt || 0)
                      .replace(/^(\D+)/, '$1 ')})`}
                  </div>
                ) : (
                  <div
                    className={
                      cellInfo?.data?.isBottomRow ? 'semiBoldText' : ''
                    }
                  >
                    {formatter
                      .format(cellInfo?.data?.totAllocatedAmt || 0)
                      .replace(/^(\D+)/, '$1 ')}
                  </div>
                )}
              </>
            );
          },
        }}
      />

      {showViewHistory && (
        <ViewHistoryComponent
          setShowViewHistory={setShowViewHistory}
          tableName="CHECK_LOG"
          keyValue={keyValue}
          isSpinner
        />
      )}
    </div>
  );
};

export default DisplayCheckTableComponent;
